import { Badge, Button } from '@auctisync/ui';
import { formatCents } from '@auctisync/shared';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  addTagToBidders,
  removeTagFromBidders,
  supabaseCrm,
  type BidderLifetimeStatsRow,
} from '../../lib/supabase-crm';

// ─── Local types ────────────────────────────────────────────────────────────

type ProfileLite = {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
};

type BidEvent = {
  kind: 'bid';
  at: string;
  amount_cents: number;
  lot_id: string;
  auction_id: string;
  is_proxy_placement: boolean;
  is_rejected: boolean;
  rejection_reason: string | null;
};

type InvoiceEvent = {
  kind: 'invoice';
  at: string;
  invoice_id: string;
  invoice_number: string | null;
  status: string;
  total_cents: number;
  auction_id: string;
};

type PaymentEvent = {
  kind: 'payment';
  at: string;
  payment_id: string;
  status: string;
  amount_cents: number;
  refunded_cents: number;
  invoice_id: string;
};

type TimelineEvent = BidEvent | InvoiceEvent | PaymentEvent;

const TIMELINE_LIMIT = 50;

/**
 * /admin/bidders/:id — Bidder CRM detail page. Module #21 Chunk 4.
 *
 * Header + LTV stats panel + tags chips (read-only this chunk) + notes
 * inline edit with auto-save + activity timeline merging bids, invoices,
 * payments. Bulk-action UI (tag add/remove, void/suspend, CSV export)
 * lands in Chunk 5.
 *
 * RLS scopes all reads + the tenant_notes write to the caller's tenant.
 */
export function AdminBidderDetail() {
  const { id: tenantBuyerId } = useParams<{ id: string }>();
  const [stats, setStats] = useState<BidderLifetimeStatsRow | null>(null);
  const [profile, setProfile] = useState<ProfileLite | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Notes inline edit state
  const [notesDraft, setNotesDraft] = useState<string>('');
  const [notesSavedAt, setNotesSavedAt] = useState<Date | null>(null);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Tax exemption lives on tenant_buyers (the stats view doesn't carry it).
  const [taxExempt, setTaxExempt] = useState(false);
  const [taxExemptReason, setTaxExemptReason] = useState('');
  const [taxSaving, setTaxSaving] = useState(false);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [taxSavedAt, setTaxSavedAt] = useState<Date | null>(null);
  const [taxLoaded, setTaxLoaded] = useState(false); // gate Save until the value is read

  // Tag editor state
  const [tagInput, setTagInput] = useState<string>('');
  const [tagBusy, setTagBusy] = useState<string | null>(null);
  const [tagError, setTagError] = useState<string | null>(null);

  // ─── Initial load ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantBuyerId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // Step 1: bidder lifetime stats row
      const { data: statsData, error: statsErr } = await supabaseCrm
        .from('bidder_lifetime_stats')
        .select('*')
        .eq('tenant_buyer_id', tenantBuyerId)
        .maybeSingle();
      if (cancelled) return;
      if (statsErr || !statsData) {
        setError(statsErr?.message ?? 'Bidder not found.');
        setLoading(false);
        return;
      }
      const statsRow = statsData as unknown as BidderLifetimeStatsRow;
      setStats(statsRow);
      setNotesDraft(statsRow.tenant_notes ?? '');

      // Seed tax-exemption from tenant_buyers (not exposed by the stats view).
      // Gate Save on a successful read so a failed seed can't persist a stale
      // "not exempt" over a truly-exempt buyer.
      const { data: tb, error: tbErr } = await supabase
        .from('tenant_buyers')
        .select('tax_exempt, tax_exempt_reason')
        .eq('id', tenantBuyerId)
        .maybeSingle();
      if (cancelled) return;
      if (tbErr) {
        setTaxError(tbErr.message);
      } else if (tb) {
        setTaxExempt(tb.tax_exempt);
        setTaxExemptReason(tb.tax_exempt_reason ?? '');
        setTaxLoaded(true);
      }

      // Step 2: profile + timeline events in parallel
      const [profRes, bidsRes, invRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, display_name, first_name, last_name, phone, city, region, country')
          .eq('id', statsRow.user_id)
          .maybeSingle(),
        supabase
          .from('bids')
          .select(
            'amount_cents, lot_id, auction_id, is_proxy_placement, is_rejected, rejection_reason, created_at',
          )
          .eq('tenant_buyer_id', tenantBuyerId)
          .order('created_at', { ascending: false })
          .limit(TIMELINE_LIMIT),
        supabase
          .from('invoices')
          .select(
            'id, invoice_number, status, total_cents, auction_id, created_at, paid_at, voided_at',
          )
          .eq('tenant_buyer_id', tenantBuyerId)
          .order('created_at', { ascending: false })
          .limit(TIMELINE_LIMIT),
      ]);
      if (cancelled) return;

      setProfile((profRes.data as unknown as ProfileLite | null) ?? null);

      const events: TimelineEvent[] = [];

      for (const b of (bidsRes.data ?? []) as Array<{
        amount_cents: number;
        lot_id: string;
        auction_id: string;
        is_proxy_placement: boolean | null;
        is_rejected: boolean | null;
        rejection_reason: string | null;
        created_at: string;
      }>) {
        events.push({
          kind: 'bid',
          at: b.created_at,
          amount_cents: b.amount_cents,
          lot_id: b.lot_id,
          auction_id: b.auction_id,
          is_proxy_placement: !!b.is_proxy_placement,
          is_rejected: !!b.is_rejected,
          rejection_reason: b.rejection_reason,
        });
      }

      const invoiceIds: string[] = [];
      for (const i of (invRes.data ?? []) as Array<{
        id: string;
        invoice_number: string | null;
        status: string;
        total_cents: number;
        auction_id: string;
        created_at: string;
        paid_at: string | null;
        voided_at: string | null;
      }>) {
        invoiceIds.push(i.id);
        // Use the most recent state-transition timestamp for chronological sort.
        // (refunded_at exists in prod from the 2026-05-10 migration but lags
        // @auctisync/shared types until next regen; created_at is the fallback.)
        const at = i.voided_at ?? i.paid_at ?? i.created_at;
        events.push({
          kind: 'invoice',
          at,
          invoice_id: i.id,
          invoice_number: i.invoice_number,
          status: i.status,
          total_cents: i.total_cents,
          auction_id: i.auction_id,
        });
      }

      if (invoiceIds.length > 0) {
        const { data: payData } = await supabase
          .from('payments')
          .select(
            'id, status, amount_cents, refunded_cents, invoice_id, created_at, succeeded_at, refunded_at',
          )
          .in('invoice_id', invoiceIds)
          .order('created_at', { ascending: false })
          .limit(TIMELINE_LIMIT);
        if (cancelled) return;
        for (const p of (payData ?? []) as Array<{
          id: string;
          status: string;
          amount_cents: number;
          refunded_cents: number | null;
          invoice_id: string;
          created_at: string;
          succeeded_at: string | null;
          refunded_at: string | null;
        }>) {
          const at = p.refunded_at ?? p.succeeded_at ?? p.created_at;
          events.push({
            kind: 'payment',
            at,
            payment_id: p.id,
            status: p.status,
            amount_cents: p.amount_cents,
            refunded_cents: p.refunded_cents ?? 0,
            invoice_id: p.invoice_id,
          });
        }
      }

      events.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
      setTimeline(events);
      setError(null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantBuyerId]);

  // ─── Notes auto-save (debounced) ────────────────────────────────────────
  const saveNotes = useCallback(
    async (next: string) => {
      if (!stats) return;
      setNotesSaving(true);
      setNotesError(null);
      const { error: updErr } = await supabase
        .from('tenant_buyers')
        .update({ tenant_notes: next.length > 0 ? next : null })
        .eq('id', stats.tenant_buyer_id);
      setNotesSaving(false);
      if (updErr) {
        setNotesError(updErr.message);
        return;
      }
      setNotesSavedAt(new Date());
    },
    [stats],
  );

  const saveTaxExemption = useCallback(async () => {
    if (!stats) return;
    setTaxSaving(true);
    setTaxError(null);
    const { error: updErr } = await supabase
      .from('tenant_buyers')
      .update({
        tax_exempt: taxExempt,
        tax_exempt_reason: taxExempt ? taxExemptReason.trim() || null : null,
      })
      .eq('id', stats.tenant_buyer_id);
    setTaxSaving(false);
    if (updErr) {
      setTaxError(updErr.message);
      return;
    }
    setTaxSavedAt(new Date());
  }, [stats, taxExempt, taxExemptReason]);

  useEffect(() => {
    if (!stats) return;
    const current = stats.tenant_notes ?? '';
    if (notesDraft === current) return;
    const timer = setTimeout(() => {
      void saveNotes(notesDraft);
    }, 800);
    return () => clearTimeout(timer);
  }, [notesDraft, stats, saveNotes]);

  // ─── Tag handlers ───────────────────────────────────────────────────────
  const handleAddTag = useCallback(async (): Promise<void> => {
    if (!stats) return;
    const raw = tagInput.trim();
    if (!raw) return;
    if ((stats.tenant_tags ?? []).includes(raw)) {
      setTagError(`Tag "${raw}" already applied.`);
      return;
    }
    setTagBusy('add');
    setTagError(null);
    const { error: rpcErr } = await addTagToBidders([stats.tenant_buyer_id], raw);
    setTagBusy(null);
    if (rpcErr) {
      setTagError(rpcErr.message);
      return;
    }
    // Optimistic local update
    setStats({
      ...stats,
      tenant_tags: [...(stats.tenant_tags ?? []), raw],
    });
    setTagInput('');
  }, [stats, tagInput]);

  const handleRemoveTag = useCallback(
    (tag: string): void => {
      if (!stats) return;
      setTagBusy(`remove:${tag}`);
      setTagError(null);
      void (async () => {
        const { error: rpcErr } = await removeTagFromBidders([stats.tenant_buyer_id], tag);
        setTagBusy(null);
        if (rpcErr) {
          setTagError(rpcErr.message);
          return;
        }
        setStats((prev) =>
          prev
            ? {
                ...prev,
                tenant_tags: (prev.tenant_tags ?? []).filter((t) => t !== tag),
              }
            : prev,
        );
      })();
    },
    [stats],
  );

  // ─── Helpers ────────────────────────────────────────────────────────────
  const displayName = useMemo((): string => {
    if (!profile) return 'Unknown bidder';
    if (profile.display_name) return profile.display_name;
    const full = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
    return full || 'Unknown bidder';
  }, [profile]);

  const statusBadgeVariant = (s: string): 'success' | 'warning' | 'outline' => {
    if (s === 'active') return 'success';
    if (s === 'pending') return 'warning';
    return 'outline';
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  if (loading) return <p style={{ color: 'var(--text-muted)' }}>Loading bidder…</p>;
  if (error)
    return (
      <div role="alert" style={errorStyle}>
        <strong>Error:</strong> {error}
        <div style={{ marginTop: '0.5rem' }}>
          <Link to="/admin/bidders">← Back to bidder list</Link>
        </div>
      </div>
    );
  if (!stats) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <Link to="/admin/bidders" style={{ fontSize: '0.875rem' }}>
          ← Back to bidders
        </Link>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>{displayName}</h1>
          {stats.tenant_bidder_number != null && (
            <span style={{ color: 'var(--text-muted)' }}>#{stats.tenant_bidder_number}</span>
          )}
          <Badge variant={statusBadgeVariant(stats.status)}>{stats.status}</Badge>
          {stats.cc_preauth_verified_at && <Badge variant="success">CC verified</Badge>}
          {stats.approved_at && <Badge variant="outline">approved</Badge>}
        </div>
        {profile && (profile.phone || profile.city || profile.region) && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            {profile.phone && <span>{profile.phone}</span>}
            {profile.phone && (profile.city || profile.region) && <span> · </span>}
            {[profile.city, profile.region, profile.country].filter(Boolean).join(', ')}
          </div>
        )}
      </header>

      {/* LTV stats panel */}
      <Section title="Lifetime stats">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.75rem',
          }}
        >
          <Stat label="Total bids" value={String(stats.total_bids)} />
          <Stat
            label="Avg bid"
            value={stats.avg_bid_cents != null ? formatCents(stats.avg_bid_cents) : '—'}
          />
          <Stat label="Total spend" value={formatCents(stats.total_spend_cents)} />
          <Stat label="Purchases" value={String(stats.total_purchases)} />
          <Stat label="Refunds" value={formatCents(stats.lifetime_refund_cents)} />
          <Stat
            label="No-show rate"
            value={stats.no_show_rate_pct != null ? `${stats.no_show_rate_pct}%` : '—'}
          />
          <Stat
            label="Days since last bid"
            value={stats.days_since_last_bid != null ? `${stats.days_since_last_bid}d` : '—'}
          />
          <Stat
            label="First bid"
            value={stats.first_bid_at ? new Date(stats.first_bid_at).toLocaleDateString() : '—'}
          />
        </div>
      </Section>

      {/* Tags chip-editor */}
      <Section title="Tags">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {(stats.tenant_tags ?? []).map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.125rem 0.5rem',
                borderRadius: '999px',
                background: 'var(--surface-alt)',
                border: '1px solid var(--admin-hairline, var(--border))',
                fontSize: '0.75rem',
              }}
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                disabled={tagBusy === `remove:${tag}`}
                onClick={() => handleRemoveTag(tag)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: tagBusy === `remove:${tag}` ? 'wait' : 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleAddTag();
            }}
            style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center' }}
          >
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add tag…"
              maxLength={50}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.25rem',
                border: '1px solid var(--admin-hairline, var(--border))',
                fontSize: '0.75rem',
                width: '8rem',
              }}
            />
            <Button type="submit" variant="ghost" size="sm" disabled={tagBusy === 'add'}>
              Add
            </Button>
          </form>
        </div>
        {tagError && (
          <div
            role="alert"
            style={{ marginTop: '0.5rem', color: 'var(--text-danger, #c33)', fontSize: '0.75rem' }}
          >
            {tagError}
          </div>
        )}
      </Section>

      {/* Notes — inline edit with auto-save */}
      <Section title="Notes">
        <textarea
          value={notesDraft}
          onChange={(e) => setNotesDraft(e.target.value)}
          placeholder="Internal notes about this bidder. Only your team sees these."
          rows={4}
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            resize: 'vertical',
          }}
        />
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
          {notesSaving
            ? 'Saving…'
            : notesError
              ? `Error: ${notesError}`
              : notesSavedAt
                ? `Saved ${notesSavedAt.toLocaleTimeString()}`
                : 'Auto-save'}
        </div>
      </Section>

      <Section title="Tax exemption">
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9rem',
            color: 'var(--text)',
          }}
        >
          <input
            type="checkbox"
            checked={taxExempt}
            onChange={(e) => setTaxExempt(e.target.checked)}
          />
          This buyer is exempt from sales tax
        </label>
        {taxExempt && (
          <input
            type="text"
            value={taxExemptReason}
            onChange={(e) => setTaxExemptReason(e.target.value)}
            placeholder="Reason (e.g., resale certificate on file)"
            style={{
              width: '100%',
              marginTop: '0.5rem',
              padding: '0.45rem 0.65rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.875rem',
            }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
          <Button
            type="button"
            size="sm"
            disabled={taxSaving || !taxLoaded}
            onClick={() => void saveTaxExemption()}
          >
            {taxSaving ? 'Saving…' : 'Save exemption'}
          </Button>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {taxError
              ? `Error: ${taxError}`
              : taxSavedAt
                ? `Saved ${taxSavedAt.toLocaleTimeString()}`
                : ''}
          </span>
        </div>
      </Section>

      {/* Activity timeline */}
      <Section title={`Activity (${timeline.length} event${timeline.length === 1 ? '' : 's'})`}>
        {timeline.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>No activity yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {timeline.map((event, idx) => (
              <li key={`${event.kind}-${idx}-${event.at}`} style={timelineItemStyle}>
                <TimelineRow event={event} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div
      style={{
        padding: '0.75rem',
        background: 'var(--surface-alt)',
        borderRadius: 'var(--r-tile, 0.375rem)',
        border: '1px solid var(--admin-hairline, var(--border))',
      }}
    >
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: '1.125rem', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {children}
    </section>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }): React.ReactElement {
  const ts = new Date(event.at).toLocaleString();

  if (event.kind === 'bid') {
    const variant = event.is_rejected ? 'error' : 'success';
    const label = event.is_rejected ? 'Bid rejected' : 'Bid placed';
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <Badge variant={variant}>{label}</Badge>
          <span style={{ marginLeft: '0.5rem' }}>{formatCents(event.amount_cents)}</span>
          {event.is_proxy_placement && (
            <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              (proxy)
            </span>
          )}
          {event.is_rejected && event.rejection_reason && (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              {event.rejection_reason}
            </div>
          )}
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{ts}</span>
      </div>
    );
  }

  if (event.kind === 'invoice') {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <Badge variant="outline">Invoice {event.status}</Badge>
          {event.invoice_number && (
            <Link to={`/admin/invoices/${event.invoice_id}`} style={{ marginLeft: '0.5rem' }}>
              #{event.invoice_number}
            </Link>
          )}
          <span style={{ marginLeft: '0.5rem' }}>{formatCents(event.total_cents)}</span>
        </div>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{ts}</span>
      </div>
    );
  }

  // payment
  const refunded = event.refunded_cents > 0;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
      <div>
        <Badge variant={refunded ? 'warning' : 'success'}>Payment {event.status}</Badge>
        <span style={{ marginLeft: '0.5rem' }}>{formatCents(event.amount_cents)}</span>
        {refunded && (
          <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            ({formatCents(event.refunded_cents)} refunded)
          </span>
        )}
      </div>
      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{ts}</span>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  padding: '1rem',
  background: 'var(--surface)',
  borderRadius: 'var(--r-card, 0.5rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 0.75rem 0',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const timelineItemStyle: React.CSSProperties = {
  padding: '0.625rem 0',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  fontSize: '0.875rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  background: 'var(--surface-alt)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
};
